import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFocusEffect } from '@react-navigation/native'
import { api } from '@api'
import type { CheckInManifestResponse, CheckInManifestTicket, CheckInSyncResponse, ScanCheckInResult } from '@hooks'
import {
  getOfflineDeviceId,
  loadManifest,
  loadQueue,
  matchOffline,
  purgeStaleCheckInData,
  saveManifest,
  saveQueue,
  type PendingCheckIn,
} from './offlineCheckInStore'

/** How often a still-pending queue retries a sync on its own, without any network event to react to (mainly native). */
const RETRY_MS = 20_000

function hasWindowEvents(): boolean {
  return typeof window !== 'undefined' && typeof window.addEventListener === 'function'
}

/** `navigator.onLine` + the 'online'/'offline' events are real in a browser (web target, and what Playwright's `setOffline` drives); native has no reliable equivalent without a new native module, so it defaults to "online" and only really finds out through a failed request. */
function useIsOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator !== 'undefined' && 'onLine' in navigator ? navigator.onLine : true,
  )
  useEffect(() => {
    if (!hasWindowEvents()) return
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])
  return online
}

/**
 * Entrance check-in, offline-capable: downloads the manifest of valid
 * tickets (never the signature secret) when the screen opens, matches scans
 * against it when a request can't reach the API, queues them locally, and
 * replays the queue once the network is back — idempotent (the API's own
 * atomic claim decides who "wins" a same-attendee conflict, this only keeps
 * the queue itself from re-growing forever).
 */
export function useOfflineCheckIn(eventId: string | undefined) {
  const deviceId = useMemo(() => getOfflineDeviceId(), [])
  const [manifest, setManifest] = useState<CheckInManifestTicket[]>(() => (eventId ? loadManifest(eventId) : []))
  const [pending, setPending] = useState<PendingCheckIn[]>(() => (eventId ? loadQueue(eventId) : []))
  const isOnline = useIsOnline()
  const manifestRef = useRef(manifest)
  manifestRef.current = manifest
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  const persistManifest = useCallback(
    (next: CheckInManifestTicket[], endsAt?: string) => {
      setManifest(next)
      if (eventId) saveManifest(eventId, next, endsAt)
    },
    [eventId],
  )
  const persistQueue = useCallback(
    (next: PendingCheckIn[]) => {
      setPending(next)
      if (eventId) saveQueue(eventId, next)
    },
    [eventId],
  )

  const refreshFromStorage = useCallback(() => {
    if (!eventId) return
    setManifest(loadManifest(eventId))
    setPending(loadQueue(eventId))
  }, [eventId])

  const downloadManifest = useCallback(async () => {
    if (!eventId) return
    try {
      const { data } = await api.get<CheckInManifestResponse>(`/organizer/events/${eventId}/check-in/manifest`)
      persistManifest(data.tickets, data.endsAt)
    } catch {
      // Offline already, or the API is unreachable: keep the last known manifest.
    }
  }, [eventId, persistManifest])

  // Several triggers (mount, focus, the 20s retry, the 'online' event) can
  // all fire close together — this guard drops an overlapping call instead
  // of letting two in-flight syncs race and resolve out of order.
  const isFlushingRef = useRef(false)

  const flush = useCallback(async () => {
    if (!eventId || pendingRef.current.length === 0 || isFlushingRef.current) return
    isFlushingRef.current = true
    const queue = pendingRef.current
    try {
      const { data } = await api.post<CheckInSyncResponse>(`/organizer/events/${eventId}/check-in/sync`, {
        deviceId,
        checkIns: queue.map(({ attendeeId, checkedInAt }) => ({ attendeeId, checkedInAt })),
      })
      const resolved = new Map(data.results.map((row) => [row.attendeeId, row.outcome]))
      // Filter against the CURRENT queue, not the snapshot taken above: a
      // scan queued while this request was in flight must survive, even
      // though it wasn't part of the batch just sent.
      persistQueue(pendingRef.current.filter((item) => !resolved.has(item.attendeeId)))
      persistManifest(
        manifestRef.current.map((ticket) => {
          const outcome = resolved.get(ticket.attendeeId)
          if (!outcome || (outcome.status !== 'checked_in' && outcome.status !== 'already_checked_in')) return ticket
          return { ...ticket, checkedInAt: outcome.checkedInAt, checkedInDeviceId: outcome.checkedInDeviceId }
        }),
      )
    } catch {
      // Still offline, or the API is unreachable: the next trigger retries.
    } finally {
      isFlushingRef.current = false
    }
  }, [eventId, deviceId, persistQueue, persistManifest])

  useEffect(() => {
    purgeStaleCheckInData()
    void downloadManifest()
    void flush()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  useFocusEffect(
    useCallback(() => {
      refreshFromStorage()
      void flush()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]),
  )

  useEffect(() => {
    if (!hasWindowEvents()) return
    const onOnline = () => void flush()
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [flush])

  useEffect(() => {
    const interval = setInterval(() => void flush(), RETRY_MS)
    return () => clearInterval(interval)
  }, [flush])

  /** Matches a scanned code against the manifest, queues it if it's a new check-in. */
  const recordOfflineScan = useCallback(
    (code: string): ScanCheckInResult => {
      const outcome = matchOffline({ code, deviceId, manifest: manifestRef.current, pending: pendingRef.current })
      if (outcome.newPending) {
        persistQueue([...pendingRef.current, outcome.newPending])
        void flush()
      }
      return outcome.result
    },
    [deviceId, persistQueue, flush],
  )

  /** Keeps the local manifest in step with a successful ONLINE scan/register, so a later offline scan of the same ticket already knows it. */
  const applyOnlineResult = useCallback(
    (result: ScanCheckInResult) => {
      if (result.status !== 'checked_in' && result.status !== 'already_checked_in') return
      const patched: CheckInManifestTicket = {
        attendeeId: result.attendeeId,
        accountId: result.accountId,
        firstName: result.firstName,
        lastName: result.lastName,
        checkedInAt: result.checkedInAt,
        checkedInDeviceId: result.checkedInDeviceId,
      }
      const idx = manifestRef.current.findIndex((ticket) => ticket.attendeeId === result.attendeeId)
      persistManifest(
        idx === -1
          ? [...manifestRef.current, patched]
          : manifestRef.current.map((ticket, i) => (i === idx ? patched : ticket)),
      )
    },
    [persistManifest],
  )

  const presentAttendeeIds = useMemo(() => {
    const ids = new Set(manifest.filter((ticket) => ticket.checkedInAt).map((ticket) => ticket.attendeeId))
    for (const item of pending) ids.add(item.attendeeId)
    return ids
  }, [manifest, pending])

  return {
    isOnline,
    pendingCount: pending.length,
    presentAttendeeIds,
    /** The last downloaded manifest — a fallback attendee list when the live one can't be fetched (offline, no cache yet). */
    manifest,
    recordOfflineScan,
    applyOnlineResult,
    refreshFromStorage,
  }
}
